import { useMemo } from 'react'
import type { FieldValues } from 'react-hook-form'
import { useSessionManager } from '@syscolect/application'
import { getDescriptorForRule } from '@syscolect/domain'
import type { EntityFormConfig } from '@/shared/entities/types'
import { isAutoCodeWrapped, withAutoCode } from './withAutoCode'

/** `Product` → `sc.product`; `ProductionOrder` → `sc.production-order`. */
export function entityUsePoint(entity: string): string {
  const kebab = entity.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase()
  return `sc.${kebab}`
}

/**
 * Descriptor-driven auto-code (pure). If `entity` has an `autoNumber` field,
 * wrap `config` with {@link withAutoCode}; otherwise return `config` intact.
 *
 * Idempotent — already-wrapped configs are returned as-is.
 */
export function applyAutoCodeConfig<TRow extends { id: string }, TForm extends FieldValues>(
  entity: string,
  config: EntityFormConfig<TRow, TForm>,
  orgId: string | undefined,
): EntityFormConfig<TRow, TForm> {
  if (isAutoCodeWrapped(config)) return config
  const descriptor = getDescriptorForRule(entity, 'auto-code')
  if (!descriptor) return config
  const autoField = descriptor.fields.find((f) => f.autoNumber === true)
  if (!autoField) return config
  return withAutoCode(config, {
    entity,
    field: autoField.name,
    orgId,
    usePoint: entityUsePoint(entity),
  })
}

/**
 * Session-aware wrapper around {@link applyAutoCodeConfig}.
 * Pass `undefined` config through (read-only PaginatedCrudPage).
 */
export function useAutoCodeConfig<TRow extends { id: string }, TForm extends FieldValues>(
  entity: string,
  config: EntityFormConfig<TRow, TForm> | undefined,
): EntityFormConfig<TRow, TForm> | undefined {
  const { currentOrganizationId } = useSessionManager()
  const orgId = currentOrganizationId ?? undefined

  return useMemo(() => {
    if (!config) return undefined
    return applyAutoCodeConfig(entity, config, orgId)
  }, [entity, config, orgId])
}
